// Descuento de accesorios que se aparta solo en la ancla (sin `lineIds`, cubre todas las piezas)
// mientras haya un accesorio que califique. Puro, sin React.
import type { ReservedLine } from '@/domains/sales/wizard/types';
import type {
    EvaluatedSalePromotion,
    EvaluateSalePromotionsResponse,
    StoreSalePromotionItem,
} from '../api/wizardPromotionsContract';
import { targetsExtra } from './extrasBenefits';
import { onlyLowersAccessories } from './phoneDraft';
import { forAnchor } from './promotionAppliesTo';
import { chosenPromotionIds, type PromotionSelection } from './promotionSelection';

/**
 * Accesorios de la venta que la promo baja: los que la evaluación ya rebaja o los que nombra su
 * alcance. Un alcance de teléfono solo dispara la promo; el servidor rebaja los accesorios.
 */
export function qualifyingExtras(
    promo: EvaluatedSalePromotion,
    extraLines: readonly ReservedLine[],
    store: readonly StoreSalePromotionItem[],
): ReservedLine[] {
    const scoped = store.find((p) => p.promotionId === promo.promotionId);
    const priced = new Set(promo.lines.filter((l) => l.discountCents > 0).map((l) => l.lineId));
    const inScope = (line: ReservedLine): boolean =>
        scoped !== undefined &&
        targetsExtra(
            { sku: line.sku, category: line.product?.category, tier: line.product?.tier ?? '' },
            scoped,
        );
    return extraLines.filter((line) => priced.has(line.lineId) || inScope(line));
}

/** Promos cuyo único efecto es bajar accesorios, por la evaluación o por la tienda. */
export function accessoryOnlyIds(
    evaluation: EvaluateSalePromotionsResponse | undefined,
    store: readonly StoreSalePromotionItem[],
): Set<string> {
    const ids = new Set(
        (evaluation?.promotions ?? []).filter(onlyLowersAccessories).map((p) => p.promotionId),
    );
    for (const p of store) {
        const { accessoryDiscount, ...rest } = p.levers;
        if (accessoryDiscount && Object.values(rest).every((v) => !v)) ids.add(p.promotionId);
    }
    return ids;
}

/** La primera (A-Z) que se puede apartar y baja algún accesorio de la venta. */
export function accessoryDiscountCandidate(
    evaluation: EvaluateSalePromotionsResponse,
    store: readonly StoreSalePromotionItem[],
    extraLines: readonly ReservedLine[],
    withoutSureKeep: boolean,
): EvaluatedSalePromotion | undefined {
    return forAnchor(
        evaluation.promotions.filter((p) => onlyLowersAccessories(p) && !p.blockedBy),
        withoutSureKeep,
    )
        .filter((p) => qualifyingExtras(p, extraLines, store).length > 0)
        .sort((a, b) => a.name.localeCompare(b.name, 'es-MX'))
        .at(0);
}

/** Las de solo accesorios apartadas en la venta: en la ancla o en la línea del accesorio. */
export function heldAccessoryDiscounts(
    selection: PromotionSelection,
    accessoryOnly: ReadonlySet<string>,
): string[] {
    return chosenPromotionIds(selection).filter((id) => accessoryOnly.has(id));
}

/** La selección sin esas promos, en cualquier línea. */
export function withoutPromotions(
    selection: PromotionSelection,
    ids: readonly string[],
): PromotionSelection {
    const byLine = Object.fromEntries(
        Object.entries(selection.byLine).map(([lineId, line]) => [
            lineId,
            line && {
                solo: line.solo && ids.includes(line.solo.promotionId) ? null : line.solo,
                stackables: line.stackables.filter((c) => !ids.includes(c.promotionId)),
            },
        ]),
    );
    return { ...selection, byLine };
}
